import type { XAxisProps, YAxisProps, YAxisTickContentProps } from 'recharts';

import { RECHARTS_COLORS } from '../../../constants/charts.js';
import { truncateLabel } from '../../../helpers/charts.js';

const MIN_CHART_HEIGHT = 600;
const CHART_HEIGHT_MULTIPLIER = 16;

type Props = {
  x: string;
  length: number;
  isCompactView: boolean;
};

type yAxisProps = YAxisProps & {
  fontSize: number;
  fill: string;
};

const useBarChart = ({ x, length, isCompactView }: Props) => {
  const tickFormatter = (tick: unknown) => {
    if (!tick) {
      return '';
    }

    if (typeof tick === 'string') {
      return truncateLabel(tick, 10);
    }

    if (typeof tick === 'number') {
      return `${tick}`;
    }

    return '';
  };

  const renderCustomTick = ({ x, y, payload }: YAxisTickContentProps) => {
    return (
      <g>
        <text
          x={+x + 15}
          y={+y - 15}
          fontSize={'11px'}
          fill={RECHARTS_COLORS.TEXT_PRIMARY}
          // @ts-expect-error TODO: fix the type of textAnchor
          textAnchor="top"
        >
          <tspan>{payload.value}</tspan>
        </text>
      </g>
    );
  };
  const xAxisPropsCompact: XAxisProps = {
    dataKey: undefined,
    type: 'number',
    tickFormatter: tickFormatter,
    tick: { fill: RECHARTS_COLORS.TEXT_PRIMARY },
    fontSize: 12,
    tickCount: length,
    interval: 'preserveStartEnd',
  };

  const xAxisPropsDefault: XAxisProps = {
    dataKey: x,
    type: 'category',
    tickFormatter: tickFormatter,
    tick: { fill: RECHARTS_COLORS.TEXT_PRIMARY },
    fontSize: 12,
    tickCount: length,
    interval: 0,
  };

  const yAxisPropsCompact: yAxisProps = {
    dataKey: x,
    type: 'category',
    tickFormatter: undefined,
    tickLine: false,
    fontSize: 12,
    fill: RECHARTS_COLORS.TEXT_PRIMARY,
    tick: renderCustomTick,
    tickCount: length,
    scale: 'auto',
  };

  const yAxisPropsDefault: yAxisProps = {
    dataKey: undefined,
    type: 'number',
    tickFormatter: tickFormatter,
    tickLine: true,
    fontSize: 12,
    fill: RECHARTS_COLORS.TEXT_PRIMARY,
    tick: {
      fill: RECHARTS_COLORS.TEXT_PRIMARY,
    },
    tickCount: length,
    scale: 'sqrt',
  };

  const xAxisProps = isCompactView ? xAxisPropsCompact : xAxisPropsDefault;
  const yAxisProps = isCompactView ? yAxisPropsCompact : yAxisPropsDefault;

  const estimatedHeight = length * CHART_HEIGHT_MULTIPLIER;

  const barChartHeight = isCompactView
    ? Math.max(estimatedHeight, MIN_CHART_HEIGHT)
    : MIN_CHART_HEIGHT;

  return { xAxisProps, yAxisProps, barChartHeight };
};

export default useBarChart;
